/**
 * MobileActionBar — bottom-docked icon bar for LabyrinthState portrait mode.
 *
 * Accepts a list of actions (icon + onPress). Renders a horizontal row of
 * square buttons. Respects safe-area-inset-bottom via the `safeBottom` option.
 */
import { Container, Graphics, Text } from 'pixi.js';
import { ACCENT, ACCENT_DIM, PANEL_FILL, FONT_SUBHEADER, TEXT_PRIMARY, TEXT_MUTED, GUGI_SHADOW } from './LayoutConstants';

export interface MobileAction {
  /** Single short glyph/emoji/letter used as the icon. */
  icon: string;
  /** Accessible short label rendered below the icon. */
  label: string;
  onPress: () => void;
  /** Optional accent tint (e.g. recall button). */
  tint?: number;
  /** If provided, the action renders as a toggle and re-paints from this predicate. */
  getActive?: () => boolean;
}

export interface MobileActionBarOptions {
  width: number;
  height?: number;
  safeBottom?: number;
}

export class MobileActionBar extends Container {
  private barW: number;
  private barH: number;
  private repaints: (() => void)[] = [];

  constructor(actions: MobileAction[], opts: MobileActionBarOptions) {
    super();
    this.barW = opts.width;
    this.barH = opts.height ?? 64;
    const pad = opts.safeBottom ?? 0;
    const fullH = this.barH + pad;

    const bg = new Graphics();
    bg.rect(0, 0, this.barW, fullH);
    bg.fill(PANEL_FILL);
    bg.moveTo(0, 0); bg.lineTo(this.barW, 0);
    bg.stroke({ color: ACCENT_DIM, width: 1 });
    bg.moveTo(0, 2); bg.lineTo(this.barW, 2);
    bg.stroke({ color: ACCENT, width: 2, alpha: 0.5 });
    this.addChild(bg);

    const cellW = this.barW / actions.length;
    for (let i = 0; i < actions.length; i++) {
      const a = actions[i]!;
      const cell = new Container();
      cell.position.set(i * cellW, 0);

      // Active-state tint chip behind the cell
      const activeBg = new Graphics();
      cell.addChild(activeBg);

      const baseTint = a.tint ?? TEXT_PRIMARY;
      const iconText = new Text({
        text: a.icon,
        style: { ...FONT_SUBHEADER, fontSize: 22, fill: baseTint, dropShadow: GUGI_SHADOW },
      });
      iconText.anchor.set(0.5, 0.5);
      iconText.position.set(cellW / 2, this.barH / 2 - 6);
      cell.addChild(iconText);

      const labelText = new Text({
        text: a.label,
        style: { ...FONT_SUBHEADER, fontSize: 9, fill: baseTint },
      });
      labelText.anchor.set(0.5, 0.5);
      labelText.position.set(cellW / 2, this.barH - 12);
      cell.addChild(labelText);

      const repaint = () => {
        const isActive = a.getActive?.() ?? false;
        activeBg.clear();
        if (isActive) {
          activeBg.rect(4, 4, cellW - 8, this.barH - 8);
          activeBg.fill({ color: ACCENT, alpha: 0.18 });
          activeBg.rect(0, 0, cellW, 3);
          activeBg.fill(ACCENT);
          iconText.style.fill = baseTint;
          labelText.style.fill = baseTint;
        } else if (a.getActive) {
          // Toggle action that's currently off — dim it
          iconText.style.fill = TEXT_MUTED;
          labelText.style.fill = TEXT_MUTED;
        }
      };
      repaint();
      this.repaints.push(repaint);

      cell.eventMode = 'static';
      cell.cursor = 'pointer';
      cell.hitArea = { contains: (x, y) => x >= 0 && x <= cellW && y >= 0 && y <= this.barH };
      cell.on('pointerdown', () => {
        a.onPress();
        // Repaint everything so cross-cell toggles can react too
        for (const r of this.repaints) r();
      });

      this.addChild(cell);
    }
  }

  /** Trigger a re-paint (e.g. after state changed externally). */
  refresh(): void {
    for (const r of this.repaints) r();
  }
}
